import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import { format } from "date-fns"
import { getBandCustomMailerTemplates } from "../../requests/bandCustomMailerTemplates"
import { Band, BandCustomMailerTemplate } from "../../types/bands"
import { Send } from "react-bootstrap-icons"
import { Concert } from "../../types/events"

interface SendAdvanceButtonProps {
  band: Band
  concert: Concert & { id: number }
}

function buildCcList(concert: Concert): string {
  const collaboratorEmails = (concert.post_collaborators || [])
    .filter((c) => c.status !== "declined")
    .map((c) => c.band_email || c.pending_band_email)

  const venueEmail = concert.bn_venue?.email || null

  return Array.from(
    new Set([...collaboratorEmails, venueEmail].filter(Boolean)),
  ).join(",")
}

function buildEmailSubject(band: Band, concert: Concert): string {
  const dateStr = concert.start_dt
    ? format(new Date(concert.start_dt), "MM-dd-yyyy")
    : ""

  return concert.start_dt
    ? `${band.name} Advance for ${dateStr} - ${concert.title}`
    : `${band.name} Advance - ${concert.title}`
}

function buildEmailBody(
  band: Band,
  concert: Concert,
  advanceText: string,
): string {
  const performances = (concert.event_performances || []).filter(
    (p) => p.status !== "declined",
  )

  const showUrl = concert.nada_url || ""
  const rawCoverImageUrl =
    concert.cover_image_url_large || concert.cover_image_url || null
  const coverImageUrl = rawCoverImageUrl
    ? rawCoverImageUrl.startsWith("http")
      ? rawCoverImageUrl
      : `${window.location.origin}${rawCoverImageUrl}`
    : null
  const backlineUrl = concert.bn_venue?.bandnada_path
    ? `${window.location.origin}${concert.bn_venue.bandnada_path}?backline=true`
    : null
  const venueAddress = concert.location?.formattedAddress || null
  const venueLine = [concert.venue_name, venueAddress]
    .filter(Boolean)
    .join(" — ")

  const bodyDateStr = concert.start_dt
    ? format(new Date(concert.start_dt), "EEEE, MMMM d, yyyy h:mma")
    : ""

  const playlistLine = concert.event_playlist_url
    ? [`Playlist: ${concert.event_playlist_url}`, ``]
    : []

  const doorsTime = concert.start_dt
    ? format(new Date(concert.start_dt), "h:mma")
    : null
  const sortedPerformances = [...performances].sort((a, b) => {
    if (a.set_time && b.set_time)
      return new Date(a.set_time).getTime() - new Date(b.set_time).getTime()
    if (a.set_time) return -1
    if (b.set_time) return 1
    return (a.performance_order ?? 0) - (b.performance_order ?? 0)
  })
  const lineupEntries = sortedPerformances
    .map((p, i) => {
      const name = p.band_name || p.pending_band_email || "TBA"
      const setTime = p.set_time ? format(new Date(p.set_time), "h:mma") : null
      return `${i + 1}. ${setTime ? `${setTime} - ` : ""}${name}`
    })
    .join("\n")
  const lineupLines = lineupEntries
    ? [``, doorsTime ? `Doors at ${doorsTime}` : `Lineup:`, lineupEntries]
    : []

  const body = [
    `Hi everyone,`,
    ``,
    `Here are the details for the show:`,
    `📅 ${bodyDateStr}`,
    `🎵 ${concert.title}`,
    ...(venueLine ? [`📍 ${venueLine}`] : []),
    `🔗 Show page: ${showUrl}`,
    ...(coverImageUrl ? [`🖼️ Flyer Download Link: ${coverImageUrl}`] : []),
    ...playlistLine,
    ...lineupLines,
    ``,
    ...(backlineUrl ? [`Backline: ${backlineUrl}`, ``] : []),
    `If you haven't signed up for bandNada yet, please do so, so that you can be added to the playlist, and people can hear your music while deciding if they want to attend.`,
    ``,
    advanceText,
    ``,
    `- ${band.name}`,
    ...(backlineUrl ? [backlineUrl] : []),
    ``,
    `(powered by bandNada)`,
  ]
    .join("\n")
    .trim()

  return body
}

function buildRecipients(concert: Concert): { to: string; cc: string } {
  const performances = (concert.event_performances || []).filter(
    (p) => p.status !== "declined",
  )
  const to = performances
    .map((p) => p.band_email || p.pending_band_email)
    .filter(Boolean)
    .join(",")
  const cc = buildCcList(concert)

  return { to, cc }
}

function buildGmailUrl(
  band: Band,
  concert: Concert,
  advanceText: string,
): string {
  const { to, cc } = buildRecipients(concert)

  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to,
    ...(cc ? { cc } : {}),
    su: buildEmailSubject(band, concert),
    body: buildEmailBody(band, concert, advanceText),
  })

  return `https://mail.google.com/mail/?${params.toString()}`
}

function buildOutlookUrl(
  band: Band,
  concert: Concert,
  advanceText: string,
): string {
  const { to, cc } = buildRecipients(concert)

  const params = new URLSearchParams({
    to,
    ...(cc ? { cc } : {}),
    subject: buildEmailSubject(band, concert),
    body: buildEmailBody(band, concert, advanceText),
  })

  return `https://outlook.live.com/mail/0/deeplink/compose?${params.toString()}`
}

function buildMailtoUrl(
  band: Band,
  concert: Concert,
  advanceText: string,
): string {
  const { to, cc } = buildRecipients(concert)

  const params = new URLSearchParams({
    ...(cc ? { cc } : {}),
    subject: buildEmailSubject(band, concert),
    body: buildEmailBody(band, concert, advanceText),
  })

  return `mailto:${to}?${params.toString()}`
}

export const SendAdvanceButton: React.FC<SendAdvanceButtonProps> = ({
  band,
  concert,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [templates, setTemplates] = useState<BandCustomMailerTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  )
  const [advanceText, setAdvanceText] = useState("")
  const [loading, setLoading] = useState(false)

  const selectTemplate = (templateId: number, list = templates) => {
    const template = list.find((t) => t.id === templateId)
    setSelectedTemplateId(templateId)
    setAdvanceText(template?.content || "")
  }

  const handleSendAdvance = async () => {
    setLoading(true)
    try {
      const response = await getBandCustomMailerTemplates(
        band.id,
        "concert_advance",
      )
      const fetchedTemplates: BandCustomMailerTemplate[] =
        response?.band_custom_mailer_templates || []
      setTemplates(fetchedTemplates)
      if (fetchedTemplates.length > 0) {
        selectTemplate(fetchedTemplates[0].id, fetchedTemplates)
      } else {
        setSelectedTemplateId(null)
        setAdvanceText("")
      }
      setShowModal(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenGmail = () => {
    setShowModal(false)
    window.open(buildGmailUrl(band, concert, advanceText), "_blank")
  }

  const handleOpenOutlook = () => {
    setShowModal(false)
    window.open(buildOutlookUrl(band, concert, advanceText), "_blank")
  }

  const handleOpenMailApp = () => {
    setShowModal(false)
    window.location.href = buildMailtoUrl(band, concert, advanceText)
  }

  return (
    <>
      <button
        className="text-sm focus:outline-none"
        onClick={handleSendAdvance}
        disabled={loading}
      >
        <Send className="inline align-baseline mx-1" size={11} />
        {loading ? "..." : "Send Advance"}
      </button>

      <Dialog
        isOpen={showModal}
        onDismiss={() => setShowModal(false)}
        aria-label="Send advance email"
        style={{ maxWidth: 560, borderRadius: 4 }}
      >
        <h2 className="font-bold text-lg mb-2">Send Advance</h2>

        <p className="text-sm text-gray-600 mb-3">
          Generate an event specific advance email using your own advance text.
        </p>

        <div className="mb-1">
          <label className="block text-xs uppercase font-bold text-gray-700 mb-1">
            Choose Template
          </label>
          {templates.length === 0 ? (
            <p className="text-sm text-gray-600">
              You haven't created any advance templates yet.
            </p>
          ) : (
            <select
              className="w-full border rounded p-2 text-sm"
              value={selectedTemplateId ?? ""}
              onChange={(e) => selectTemplate(Number(e.target.value))}
            >
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <p className="text-sm mb-3">
          <Link
            to="/dashboard/mailer_templates"
            className="text-blue-700 hover:underline"
            onClick={() => setShowModal(false)}
          >
            Create/Edit templates
          </Link>
        </p>

        <p className="text-sm text-gray-600 mb-2">
          This just generates the email draft for you. It doesn't send anything,
          and you can edit as much as you like before sending.
        </p>

        {templates.length > 0 && (
          <textarea
            className="w-full border rounded p-2 text-sm font-mono bg-gray-50 text-gray-700"
            rows={10}
            value={buildEmailBody(band, concert, advanceText)}
            readOnly
          />
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-4 py-2 text-sm text-gray-600 hover:underline"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </button>
          {templates.length > 0 && (
            <>
              <button
                className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50"
                onClick={handleOpenMailApp}
              >
                Mail App
              </button>
              <button
                className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50"
                onClick={handleOpenOutlook}
              >
                Outlook
              </button>
              <button
                className="px-4 py-2 text-sm bg-yellow-600 text-white font-semibold rounded hover:bg-yellow-700"
                onClick={handleOpenGmail}
              >
                Open Gmail
              </button>
            </>
          )}
        </div>
      </Dialog>
    </>
  )
}
